import { FeedbackByForm } from "components/feedback";
import QuestionHeader from "components/feedback/QuestionHeader";
import TextFeedback from "components/feedback/TextFeedback";
import UserProfileCard from "components/feedback/UserProfileCard";
import { QuestionType } from "enums/form";
import { useMemo } from "react";
import Markdown from "react-markdown";
import { Feedback } from "types/feedback";
import { Question } from "types/question";
import { FeedbackUtil, UserUtil } from "utils";

type FeedbackSheetContentProps = {
  feedbacks: Feedback[] | undefined;
  emptyMessage?: string;
  questions: Question[];
};

const FeedbackSheetContent = ({
  feedbacks,
  emptyMessage = "There is no feedback for this cycle.",
  questions = [],
}: FeedbackSheetContentProps) => {
  const multipleChoiceQuestions = useMemo(
    () =>
      questions.filter(
        (question) => question.questionType === QuestionType.MULTIPLE_CHOICE,
      ),
    [questions],
  );

  const answersGroupedByQuestion = useMemo(
    () => (feedbacks ? FeedbackUtil.groupByQuestion(feedbacks) : {}),
    [feedbacks],
  );

  if (!feedbacks?.length) {
    return <p className="body-14-regular text-neutral-50">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-4">
      {multipleChoiceQuestions.length > 0 && (
        <FeedbackByForm
        questions={multipleChoiceQuestions}
        answersGroupedByQuestion={answersGroupedByQuestion}
          usePieChart
          className="rounded-2xl bg-blue-10 p-6"
        />
      )}

      {feedbacks.map((feedback) => {
        const sortedAnswers = [...feedback.answers]
          .filter(
            (answer) =>
              answer.question.questionType !== QuestionType.MULTIPLE_CHOICE,
          )
          .sort((a, b) => a.question.sequence - b.question.sequence);

        if (!sortedAnswers.length) {
          return null;
        }

        return (
          <article key={feedback.id} className="space-y-5 rounded-2xl bg-blue-10 p-6">
            <div className="space-y-1">
              <p className="body-12-regular text-neutral-50">Reviewer:</p>
              <UserProfileCard
                userProfile={UserUtil.getReviewerInfoToDisplay(feedback.reviewer)}
              />
            </div>

            <div className="space-y-5 border-t border-neutral-20 pt-5">
              {sortedAnswers.map((answer) => {
                const { question } = answer;

                return (
                  <div key={answer.id}>
                    <QuestionHeader
                      sequence={question.sequence}
                      label={question.label}
                      hint={question.hint}
                    />
                    {question.questionType === QuestionType.TEXT ? (
                      <TextFeedback
                        answer={answer}
                        className="body-14-regular break-words text-neutral-200"
                      />
                    ) : (
                      <p className="body-14-regular break-words text-neutral-200">
                        <Markdown>{answer.answerOptions?.label ?? "—"}</Markdown>
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default FeedbackSheetContent;
